import { useState } from "react";
import toast from "react-hot-toast";

import { useExpedite } from "@web-app/api";
import {
  Avatar,
  Button,
  ConfirmModal,
  DateTimeTz,
  Icon,
  LoadingIcon,
  RefreshButton,
} from "@web-app/components";
import { ExpediteDelivery } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

const ExpediteDeliveryDetails = ({
  delivery,
  timezone,
}: {
  delivery: ExpediteDelivery;
  timezone: keyof typeof timeZoneMap;
}) => {
  return (
    <div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Fee</span>
          <span className="tw-details-field-value flex items-center gap-2">
            {`$${delivery.delivery_fee}`}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Tip</span>
          <span className="tw-details-field-value flex items-center gap-2">
            {`$${delivery.tip}`}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">External Delivery ID</span>
          <span className="tw-details-field-value flex items-center gap-2">
            {delivery.external_delivery_id}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        {delivery.tasks.map((task) => {
          return (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">
                <Icon
                  name={
                    task.type === "pickup"
                      ? "arrowRightFromBracket"
                      : "arrowRightToBracket"
                  }
                />
                {` ${task.street1.slice(0, 15) + "..."}`}
              </span>
              <span className="tw-details-field-value">
                <DateTimeTz
                  start={new Date(task.event_at + "Z")}
                  timezone={timezone}
                  presentation="default"
                />
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title flex items-center">
            Assignee
          </span>
          <span className="flex items-center gap-2">
            {!delivery.driver && (
              <span className="tw-details-field-value">No Assignee</span>
            )}
            {delivery.driver?.first_name && (
              <>
                <span>
                  <Avatar image={undefined} size={10} />
                </span>
                <span className="flex flex-col">
                  <span className="tw-details-field-value !font-semibold break-words">
                    {delivery.driver?.first_name +
                      " " +
                      delivery.driver?.last_name}
                  </span>
                </span>
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function ExpediteDeliveryInfo({
  entity,
  entityId,
  timezone,
}: {
  entity: "order" | "route";
  entityId: string;
  timezone: keyof typeof timeZoneMap;
}) {
  const {
    data: delivery,
    isLoading,
    refetch,
    isRefetching,
  } = useExpedite(entityId, entity);

  const [isCancelling, setIsCancelling] = useState(false);
  const [isCanceled, setIsCancelled] = useState(false);

  if (isLoading || isRefetching)
    return <LoadingIcon className="w-8 h-8 m-auto mt-10" />;

  if (isCanceled)
    return (
      <div className="gap-2 px-4 py-4 border-b border-gray-200">
        The expedite delivery has been canceled successfully. You may create the
        delivery again if required.
      </div>
    );
  if (!delivery) return <div>No expedite delivery is found</div>;

  const handleCancel = async () => {
    const resp = await http.post(
      `/integrations/expedite/cancel?id=${entityId}&entity=${entity}`,
    );
    if (resp.status === 200) {
      toast.success("Delivery canceled successfully");
      refetch();
      setIsCancelling(false);
      setIsCancelled(true);
    }
  };

  const isDeliveryCompleted = () => {
    const lastTask = delivery.data.tasks[delivery.data.tasks.length - 1];
    if (lastTask.job_status == 2) {
      return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-end mr-5 gap-2">
        <RefreshButton onClick={() => refetch()} />
        <ConfirmModal
          title={
            <div className="flex items-center gap-4">
              <Icon
                name="fasCircleExclamation"
                className="w-6 h-6 text-blue-500"
              />
              <span>Cancel Expedite Delivery</span>
            </div>
          }
          subtitle="A cancellation fee may be applied by Expedite for cancelling this delivery. Are you sure you want to proceed?"
          isOpen={isCancelling}
          actions={[
            {
              label: "Yes",
              onClick: handleCancel,
              size: "sm",
            },
            {
              label: "No",
              onClick: () => setIsCancelling(false),
              plain: true,
              size: "sm",
            },
          ]}
          onClose={() => setIsCancelling(false)}
        />
        <Button
          size="sm"
          onClick={() => setIsCancelling(true)}
          color="red"
          disabled={isDeliveryCompleted()}
          isLoading={isCancelling}
        >
          {isCancelling ? "Cancelling..." : "Cancel Delivery"}
        </Button>
      </div>
      <ExpediteDeliveryDetails delivery={delivery.data} timezone={timezone} />
    </div>
  );
}
